import React from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';
import type { SvgIconComponent } from '@mui/icons-material';

interface SingleSelectProps {
    options: { icon?: SvgIconComponent; label: string; value: string }[];
    value: string;
    onChange: (value: string) => void;
    multi?: false;
}

interface MultiSelectProps {
    options: { icon?: SvgIconComponent; label: string; value: string }[];
    value: string[];
    onChange: (value: string[]) => void;
    multi: true;
}

type RToggleGroupProps = SingleSelectProps | MultiSelectProps;

export function RToggleGroup(props: RToggleGroupProps) {
    const { options, multi } = props;

    const isSelected = (optionValue: string) => {
        if (multi) return (props.value as string[]).includes(optionValue);
        return optionValue === props.value;
    };

    const handleClick = (optionValue: string) => {
        if (multi) {
            const current = props.value as string[];
            const onChange = props.onChange as (value: string[]) => void;
            if (current.includes(optionValue)) {
                onChange(current.filter((v) => v !== optionValue));
            } else {
                onChange([...current, optionValue]);
            }
        } else {
            (props.onChange as (value: string) => void)(optionValue);
        }
    };

    return (
        <Container>
            {options.map((option) => (
                <ToggleButton
                    key={option.value}
                    selected={isSelected(option.value)}
                    onClick={() => handleClick(option.value)}
                >
                    {option.icon && <option.icon sx={{ fontSize: 14 }} />}
                    {option.label}
                </ToggleButton>
            ))}
        </Container>
    );
}

const Container = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
});

const ToggleButton = styled('button')<{ selected: boolean }>(({ selected }) => ({
    padding: '0px 10px',
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'inherit',
    height: 24,
    borderRadius: 30,
    border: selected ? `2px solid ${Colors.turquoise}` : `1px solid ${Colors.lightGrey}`,
    backgroundColor: selected ? Colors.turquoise : Colors.faintGrey,
    color: selected ? Colors.white : Colors.mediumGrey,
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.15s ease',
    '&:focus, &:focus-visible': {
        outline: 'none',
        boxShadow: 'none',
    },
    '&:hover': {
        borderColor: Colors.turquoise,
    },
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
}));
